import axiosInstance from '../config/axios.js';

const API_BASE = '/whatsapp-payments';
const COUNTER_API = '/company-whatsapp-counter';

const whatsappPaymentService = {
  // ==================== Payment Operations ====================
  
  // Get all payments for a company
  getPaymentsByCompany: async (companyId, status = null) => {
    const params = status ? { status } : {};
    const response = await axiosInstance.get(`${API_BASE}/company/${companyId}`, { params });
    return response.data;
  },

  // Get payment by ID
  getPaymentById: async (paymentId) => {
    const response = await axiosInstance.get(`${API_BASE}/${paymentId}`);
    return response.data;
  },

  // Get all payments (System Admin only)
  getAll: async (params = {}) => {
    const response = await axiosInstance.get(API_BASE, { params });
    return response.data;
  },

  // Create a new payment with receipt
  createPayment: async (paymentData, receiptFile = null) => {
    const formData = new FormData();
    
    // Append payment data
    Object.keys(paymentData).forEach(key => {
      if (paymentData[key] !== null && paymentData[key] !== undefined) {
        formData.append(key, paymentData[key]);
      }
    });

    // Append receipt if provided
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }

    const response = await axiosInstance.post(API_BASE, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Update a payment
  updatePayment: async (paymentId, paymentData, receiptFile = null) => {
    const formData = new FormData();
    
    // Append payment data
    Object.keys(paymentData).forEach(key => {
      if (paymentData[key] !== null && paymentData[key] !== undefined) {
        formData.append(key, paymentData[key]);
      }
    });

    // Append receipt if provided
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }

    const response = await axiosInstance.put(`${API_BASE}/${paymentId}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
    return response.data;
  },

  // Delete a payment
  deletePayment: async (paymentId) => {
    const response = await axiosInstance.delete(`${API_BASE}/${paymentId}`);
    return response.data;
  },

  // Mark payment as completed
  completePayment: async (paymentId, verifiedBy = null) => {
    const response = await axiosInstance.post(`${API_BASE}/${paymentId}/complete`, {
      verified_by: verifiedBy
    });
    return response.data;
  },

  // Mark payment as failed
  failPayment: async (paymentId, remarks = null) => {
    const response = await axiosInstance.post(`${API_BASE}/${paymentId}/fail`, {
      remarks
    });
    return response.data;
  },

  // Download receipt
  downloadReceipt: async (paymentId) => {
    const response = await axiosInstance.get(`${API_BASE}/${paymentId}/receipt`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get receipt URL
  getReceiptUrl: (paymentId) => {
    return `${axiosInstance.defaults.baseURL}${API_BASE}/${paymentId}/receipt`;
  },

  // Quick payment (without receipt) - simplified endpoint
  quickPayment: async (companyId, amount, paymentMethod = 'cash', transactionId = null, remarks = null) => {
    const response = await axiosInstance.post(`${COUNTER_API}/${companyId}/payment`, {
      amount,
      payment_method: paymentMethod,
      transaction_id: transactionId,
      remarks
    });
    return response.data;
  },

  // Get pending payments
  getPendingPayments: async (companyId = null) => {
    const params = companyId ? { company_id: companyId } : {};
    const response = await axiosInstance.get(`${API_BASE}/pending`, { params });
    return response.data;
  },

  // Get completed payments
  getCompletedPayments: async (companyId = null, startDate = null, endDate = null) => {
    const params = {};
    if (companyId) params.company_id = companyId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get(`${API_BASE}/completed`, { params });
    return response.data;
  },

  // Get payment summary/statistics
  getPaymentSummary: async (companyId = null, startDate = null, endDate = null) => {
    const params = {};
    if (companyId) params.company_id = companyId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const response = await axiosInstance.get(`${API_BASE}/summary`, { params });
    return response.data;
  },

  // ==================== Utility Functions ====================

  // Get payment method display name
  getPaymentMethodDisplayName: (method) => {
    const methodNames = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'cheque': 'Cheque',
      'upi': 'UPI',
      'online': 'Online',
      'other': 'Other'
    };
    return methodNames[method] || method;
  },

  // Get payment status display name
  getPaymentStatusDisplayName: (status) => {
    const statusNames = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed',
      'refunded': 'Refunded'
    };
    return statusNames[status] || status;
  },

  // Format currency for display
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  },

  // Format date for display
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
};

export default whatsappPaymentService;
